import { useCallback, useEffect, useRef, useState } from 'react'

// eslint-disable-next-line
// @ts-ignore
import callJoinSound from '../../assets/audio/call_join_sound.mp3'
// eslint-disable-next-line
// @ts-ignore
import callLeaveSound from '../../assets/audio/call_leave_sound.mp3'
// eslint-disable-next-line
// @ts-ignore
import callOutgoingRingSound from '../../assets/audio/call_outgoing_ring.mp3'
// eslint-disable-next-line
// @ts-ignore
import notificationSound from '../../assets/audio/notification_sound.mp3'
// eslint-disable-next-line
// @ts-ignore
import ringingSound from '../../assets/audio/ringing_sound.mp3'

export type AudioManagerType =
  | {
      stopAllLoops: () => void
      playMessageSound: () => void
      playRingingSound: () => void
      playOutgoingCallSound: () => void
      playJoinedCallSound: () => void
      playLeftCallSound: () => void
    }
  | Record<string, never>

const CALL_RINGING = 'CALL_RINGING'
const CALL_OUTGOING = 'CALL_OUTGOING'

export const useAudioManager = (): AudioManagerType => {
  const loopsPlaying = useRef<{
    [id: string]:
      | { element: HTMLAudioElement; playPromise: any; timer: any }
      | Record<string, never>
  }>({})
  const [playLoopQueue, setPlayLoopQueue] = useState<
    { id: string; element: HTMLAudioElement; loopForMs: number }[]
  >([])
  const [initializingLoop, setInitializingLoop] = useState(false)

  useEffect(() => {
    const getAudioPermissionForSafari = () => {
      const element = new Audio('')
      element.remove()
    }

    window.addEventListener('click', getAudioPermissionForSafari, {
      once: true,
    })
  }, [])

  const stopAllLoops = useCallback(async () => {
    const ids = Object.keys(loopsPlaying.current)
    for (let i = 0; i < ids.length; i++) {
      await stopLoop(ids[i])
    }
    return Promise.resolve()
  }, [])

  const initializeNextLoop = useCallback(
    async ({ id, element, loopForMs }) => {
      await stopAllLoops()
      loopsPlaying.current[id] = {}
      loopsPlaying.current[id].element = element
      loopsPlaying.current[id].playPromise = element.play()

      loopsPlaying.current[id].timer = setTimeout(() => {
        stopLoop(id)
      }, loopForMs)
      setPlayLoopQueue((prevState) => {
        return prevState.slice(1, prevState.length)
      })
      setInitializingLoop(false)
    },
    [stopAllLoops],
  )

  useEffect(() => {
    if (!initializingLoop && playLoopQueue.length) {
      setInitializingLoop(true)
      initializeNextLoop(playLoopQueue[0])
    }
  }, [initializeNextLoop, initializingLoop, playLoopQueue])

  const createAudio = (sound: any, options: { [key: string]: any }) => {
    const element: HTMLAudioElement = new Audio(sound)
    Object.keys(options).forEach((key) => {
      // eslint-disable-next-line
      // @ts-ignore
      element[key] = options[key]
    })
    return element
  }

  const playOneShot = (element: HTMLAudioElement) => {
    element.addEventListener(
      'ended',
      () => {
        element.remove()
      },
      {
        once: true,
      },
    )
    element.play()
  }

  const playLoop = async (
    id: string,
    element: HTMLAudioElement,
    loopForMs: number,
  ) => {
    setPlayLoopQueue((prevState: any) => {
      return [...prevState, { id, element, loopForMs }]
    })
  }

  const stopLoop = async (id: string) => {
    if (loopsPlaying.current[id]) {
      const { element, playPromise, timer } = loopsPlaying.current[id] || {}
      if (element) {
        await playPromise
        element.pause()
        element.remove()
      }
      if (timer) {
        clearTimeout(timer)
      }
      delete loopsPlaying.current[id]
    }
    return Promise.resolve()
  }

  const playMessageSound = useCallback(() => {
    const element = createAudio(notificationSound, {
      volume: 0.5,
    })
    playOneShot(element)
  }, [])

  const playRingingSound = useCallback(() => {
    const element = createAudio(ringingSound, {
      volume: 0.2,
      loop: true,
    })
    return playLoop(CALL_RINGING, element, 10000)
  }, [])

  const playOutgoingCallSound = useCallback(() => {
    const element = createAudio(callOutgoingRingSound, {
      volume: 0.2,
      loop: true,
    })
    return playLoop(CALL_OUTGOING, element, 10000)
  }, [])

  const playJoinedCallSound = useCallback(() => {
    const element = createAudio(callJoinSound, {
      volume: 0.05,
    })
    playOneShot(element)
  }, [])

  const playLeftCallSound = useCallback(() => {
    const element = createAudio(callLeaveSound, {
      volume: 0.1,
    })
    playOneShot(element)
  }, [])

  return {
    stopAllLoops,
    playMessageSound,
    playRingingSound,
    playOutgoingCallSound,
    playJoinedCallSound,
    playLeftCallSound,
  }
}
